import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, CircularProgress, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Switch, Avatar, Button,
  TablePagination, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Chip, TextField, Tab, Tabs, MenuItem, Checkbox, FormControlLabel, Menu, ListItemIcon, ListItemText, Divider, Badge, InputAdornment } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import HistoryIcon from '@mui/icons-material/History';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CloseIcon from '@mui/icons-material/Close';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import { driverService } from '../services/driverService';
import { Driver, DriverAssignmentMode, DriverShift, OrderHistoryFilters } from '../models/Driver'; // Import interfaces
import DriverFormDialog from '../components/DriverFormDialog'; // Import the form dialog component
import DriverDetailsDialog from '../components/DriverDetailsDialog'; // Import the details dialog component
import DriversLiveMapModal from '../components/DriversLiveMapModal';
import DriverLiveTrackingModal from '../components/DriverLiveTrackingModal';
import TuneIcon from '@mui/icons-material/Tune';
import DeliveryOfferSettingsDialog from '../components/DeliveryOfferSettingsDialog';
import { useAdminScope } from '../contexts/AdminScopeContext';
import ScopeRequirementAlert from '../components/ScopeRequirementAlert';
import PendingDriverRequestsPanel from '../components/PendingDriverRequestsPanel';

const getActivityStatusMeta = (status?: Driver['activityStatus']) => {
  switch (status) {
    case 'delivering':
      return { label: 'En livraison', color: 'warning.main' };
    case 'returning':
      return { label: 'Retour', color: 'info.main' };
    case 'free':
    default:
      return { label: 'Libre', color: 'success.main' };
  }
};

type DriverViewTab = 'groupDrivers' | 'pendingRequests';

const normalizeAssignmentMode = (driver?: Driver | null): DriverAssignmentMode =>
  driver?.assignmentMode === 'store_locked' ? 'store_locked' : 'group_shared';

const formatShiftDuration = (startedAt?: DriverShift['startedAt']): string => {
  if (!startedAt) return '';
  const startDate = typeof (startedAt as any).toDate === 'function'
    ? (startedAt as any).toDate() as Date
    : new Date(((startedAt as any).seconds ?? 0) * 1000);
  const diffMs = Date.now() - startDate.getTime();
  const totalMinutes = Math.max(0, Math.floor(diffMs / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h${minutes.toString().padStart(2, '0')}`;
  return `${minutes} min`;
};

type DriverColumnKey =
  | 'online' | 'activity' | 'shift' | 'enabled'
  | 'rating' | 'orders' | 'assignment' | 'commission' | 'status';

const DRIVER_COLUMNS: { key: DriverColumnKey; label: string }[] = [
  { key: 'online',     label: 'En ligne' },
  { key: 'activity',   label: 'Activité' },
  { key: 'shift',      label: 'Disponibilité' },
  { key: 'enabled',    label: 'Activé' },
  { key: 'rating',     label: 'Note' },
  { key: 'orders',     label: 'Commandes' },
  { key: 'assignment', label: 'Affectation' },
  { key: 'commission', label: 'Commission' },
  { key: 'status',     label: 'Statut' },
];

const DEFAULT_VISIBLE_COLUMNS: Record<DriverColumnKey, boolean> = {
  online: true, activity: true, shift: true, enabled: true,
  rating: true, orders: true, assignment: true, commission: true, status: true,
};

const COLUMN_STORAGE_KEY = 'drivers_table_visible_columns_v1';

const Drivers: React.FC = () => {
  const { activeScopeType, selectedGroupId, selectedStoreId, stores, operationGroups } = useAdminScope();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [activeTab, setActiveTab] = useState<DriverViewTab>(() =>
    activeScopeType === 'group' && selectedGroupId ? 'groupDrivers' : 'pendingRequests'
  );

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlineOnly, setShowOnlineOnly] = useState(false);
  const [showOpenShiftOnly, setShowOpenShiftOnly] = useState(false);
  const [openShiftsByDriverId, setOpenShiftsByDriverId] = useState<Record<string, DriverShift | null>>({});

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false); // State for form dialog visibility
  const [currentDriver, setCurrentDriver] = useState<Driver | undefined>(undefined); // State for driver being edited in form
  const [isSubmitting, setIsSubmitting] = useState(false); // State for form submission loading
  const [submitError, setSubmitError] = useState<string | null>(null); // State for form submission error

  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false); // State for details dialog visibility
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null); // State for driver being viewed in details dialog

  // Ban/Unban dialog states
  const [isBanDialogOpen, setIsBanDialogOpen] = useState(false);
  const [isUnbanDialogOpen, setIsUnbanDialogOpen] = useState(false);
  const [driverToBan, setDriverToBan] = useState<Driver | null>(null);
  const [driverToUnban, setDriverToUnban] = useState<Driver | null>(null);
  const [isBanning, setIsBanning] = useState(false);
  const [isUnbanning, setIsUnbanning] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [unbanReason, setUnbanReason] = useState('');

  // Map modal states
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isLiveTrackingOpen, setIsLiveTrackingOpen] = useState(false);
  const [liveTrackingDriver, setLiveTrackingDriver] = useState<Driver | null>(null);

  // Delivery offer settings dialog
  const [isOfferSettingsOpen, setIsOfferSettingsOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [driverToAssign, setDriverToAssign] = useState<Driver | null>(null);
  const [assignmentStoreId, setAssignmentStoreId] = useState('');
  const [assignmentMode, setAssignmentMode] = useState<DriverAssignmentMode>('group_shared');
  const [isAssignmentSubmitting, setIsAssignmentSubmitting] = useState(false);

  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuDriver, setActionMenuDriver] = useState<Driver | null>(null);

  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState<Record<DriverColumnKey, boolean>>(() => {
    try {
      const stored = typeof window !== 'undefined' ? window.localStorage.getItem(COLUMN_STORAGE_KEY) : null;
      if (stored) return { ...DEFAULT_VISIBLE_COLUMNS, ...JSON.parse(stored) };
    } catch { /* ignore */ }
    return DEFAULT_VISIBLE_COLUMNS;
  });

  useEffect(() => {
    try { window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibleColumns)); } catch { /* ignore */ }
  }, [visibleColumns]);

  const toggleColumn = (key: DriverColumnKey) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [, setDurationTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setDurationTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, driver: Driver) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuDriver(driver);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuDriver(null);
  };

  const isGroupScopeReady = activeScopeType === 'group' && !!selectedGroupId;
  const storesById = new Map(stores.map((store) => [store.id, store]));

  const formatStoreLabel = (storeId?: string | null): string => {
    if (!storeId) {
      return 'Aucun magasin';
    }

    return storesById.get(storeId)?.name || storeId;
  };

  const formatGroupLabel = (groupId?: string | null): string => {
    if (!groupId) {
      return 'Sans groupe';
    }

    return operationGroups.find((group) => group.id === groupId)?.name || groupId;
  };

  const resolveDefaultAssignmentStoreId = (driver?: Driver | null): string => {
    const preferredStoreId =
      driver?.assignedStoreId ||
      selectedStoreId ||
      stores.find((store) => store.operationGroupId === (driver?.groupId || selectedGroupId))?.id ||
      stores[0]?.id ||
      '';

    return preferredStoreId;
  };

  // Handle pagination changes
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const filteredDrivers = drivers.filter((driver) => {
    const fullName = `${driver.firstName || ''} ${driver.lastName || ''}`.trim().toLowerCase();
    const matchesSearch =
      normalizedSearchQuery.length === 0 ||
      fullName.includes(normalizedSearchQuery);
    const matchesOnline = !showOnlineOnly || driver.availability === 'online';
    const matchesOpenShift = !showOpenShiftOnly || !!openShiftsByDriverId[driver.id];

    return matchesSearch && matchesOnline && matchesOpenShift;
  });

  // Calculate the slice of drivers to display based on pagination
  const displayedDrivers = filteredDrivers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      setError(null);
      const driversList = await driverService.getDrivers();
      setDrivers(driversList);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequestsCount = async () => {
    try {
      const pendingRequests = await driverService.getPendingDriverRequests();
      setPendingRequestsCount(pendingRequests.length);
    } catch (err) {
      console.error('Error fetching pending driver requests count:', err);
    }
  };

  useEffect(() => {
    if (isGroupScopeReady) {
      void fetchDrivers();
      return;
    }

    setDrivers([]);
    setOpenShiftsByDriverId({});
    setLoading(false);
  }, [isGroupScopeReady, selectedGroupId]);

  useEffect(() => {
    void fetchPendingRequestsCount();
  }, []);

  useEffect(() => {
    if (!isGroupScopeReady && activeTab === 'groupDrivers') {
      setActiveTab('pendingRequests');
    }
  }, [activeTab, isGroupScopeReady]);

  useEffect(() => {
    setPage(0);
  }, [activeTab, searchQuery, showOnlineOnly, showOpenShiftOnly]);

  useEffect(() => {
    const loadOpenShifts = async () => {
      try {
        const shiftEntries = await Promise.all(
          drivers.map(async (driver) => {
            try {
              const shift = await driverService.getOpenDriverShift(driver.id);
              return [driver.id, shift] as const;
            } catch (err) {
              console.error(`Error fetching open shift for driver ${driver.id}:`, err);
              return [driver.id, null] as const;
            }
          })
        );

        setOpenShiftsByDriverId(Object.fromEntries(shiftEntries));
      } catch (err) {
        console.error('Error fetching open shifts for drivers:', err);
      }
    };

    if (drivers.length === 0) {
      setOpenShiftsByDriverId({});
      return;
    }

    void loadOpenShifts();
  }, [drivers]);

  const handleToggleEnabled = async (driverId: string, enabled: boolean) => {
    try {
      const targetDriver = drivers.find((driver) => driver.id === driverId);
      if (targetDriver?.isSuspended) {
        setError('Le livreur est banni. Utilisez "Débannir" au lieu du switch Actif.');
        return;
      }
      // Optimistically update UI
      setDrivers(drivers.map(driver =>
        driver.id === driverId ? { ...driver, enabled: enabled } : driver
      ));
      await driverService.updateEnabledStatus(driverId, enabled);
    } catch (err: any) {
      setError(err.message);
      console.error('Error updating driver enabled status:', err);
      // Revert UI on error
      setDrivers(drivers.map(driver =>
        driver.id === driverId ? { ...driver, enabled: !enabled } : driver
      ));
    }
  };

  const handleToggleFavorite = async (driverId: string, isFavorite: boolean) => {
    try {
      // Optimistically update UI
      setDrivers(drivers.map(driver =>
        driver.id === driverId ? { ...driver, is_favorite: isFavorite } : driver
      ));
      await driverService.updateFavoriteStatus(driverId, isFavorite);
    } catch (err: any) {
      setError(err.message);
      console.error('Error updating driver favorite status:', err);
      // Revert UI on error
      setDrivers(drivers.map(driver =>
        driver.id === driverId ? { ...driver, is_favorite: !isFavorite } : driver
      ));
    }
  };

  // Set driver availability (online/offline)
  const handleSetAvailability = async (driverId: string, availability: 'online' | 'offline') => {
    try {
      const targetDriver = drivers.find((driver) => driver.id === driverId);
      if (targetDriver?.isSuspended) {
        setError("Le livreur est banni. Sa disponibilité ne peut pas être modifiée.");
        return;
      }
      // Optimistic UI update
      setDrivers(drivers.map(driver =>
        driver.id === driverId ? { ...driver, availability } : driver
      ));
      await driverService.updateAvailability(driverId, availability);
    } catch (err: any) {
      setError(err.message);
      console.error('Error updating driver availability:', err);
      // On error, refetch to restore correct state
      fetchDrivers();
    }
  };

  const handleSetActivityStatusFree = async (driver: Driver) => {
    const currentStatus = driver.activityStatus ?? 'free';
    if (currentStatus === 'free') return;

    try {
      // Optimistic UI update
      setDrivers((prev) =>
        prev.map((d) =>
          d.id === driver.id
            ? { ...d, activityStatus: 'free', currentOrderId: null, currentOrderIds: [], currentUserIds: [] }
            : d
        )
      );
      setSelectedDriver((prev) =>
        prev && prev.id === driver.id
          ? { ...prev, activityStatus: 'free', currentOrderId: null, currentOrderIds: [], currentUserIds: [] }
          : prev
      );

      await driverService.setActivityStatusFree(driver.id);
    } catch (err: any) {
      setError(err.message);
      console.error('Error updating driver activity status:', err);
      fetchDrivers();
    }
  };

  const handleDeleteDriver = async (driverId: string) => {
    if (window.confirm('Are you sure you want to delete this driver?')) {
      try {
        // Optimistically update UI
        setDrivers(drivers.filter(driver => driver.id !== driverId));
        await driverService.deleteDriver(driverId);
        // Re-fetch drivers to ensure data consistency after deletion
        fetchDrivers();
      } catch (err: any) {
        setError(err.message);
        console.error('Error deleting driver:', err);
        // Revert UI on error (requires re-fetching or storing original list)
        // For simplicity, we might just show an error message and ask user to refresh
        alert(`Failed to delete driver: ${err.message}`);
        // A more robust solution would involve adding the driver back to the state
      }
    }
  };

  const handleOpenAddDialog = () => {
    setCurrentDriver(undefined); // Clear current driver for add mode
    setSubmitError(null); // Clear previous submission errors
    setIsFormDialogOpen(true);
  };

  const handleOpenEditDialog = (driver: Driver) => {
    setCurrentDriver(driver); // Set current driver for edit mode
    setSubmitError(null); // Clear previous submission errors
    setIsFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setIsFormDialogOpen(false);
    setCurrentDriver(undefined); // Clear current driver on close
    setSubmitError(null); // Clear submission errors on close
  };

  const handleSubmitForm = async (formData: any, photoFile?: File | null) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (currentDriver) {
        // Editing existing driver
        await driverService.updateDriver(currentDriver.id, formData, photoFile); // Pass photoFile
      } else {
        // Adding new driver
        await driverService.addDriver(formData, photoFile); // Pass photoFile
      }
      // Close dialog and refresh driver list on success
      handleCloseFormDialog();
      fetchDrivers();
    } catch (err: any) {
      setSubmitError(err.message);
      console.error('Error submitting driver form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDetails = (driver: Driver) => {
    setSelectedDriver(driver); // Set the driver for the details dialog
    setIsDetailsDialogOpen(true); // Open the details dialog
  };

  const handleCloseDetailsDialog = () => {
    setIsDetailsDialogOpen(false);
    setSelectedDriver(null); // Clear selected driver on close
  };

  const handleResetDriverCounter = async (
    driverId: string,
    counterField: 'orders_accepted_count' | 'orders_ignored_count' | 'orders_refused_count'
  ) => {
    // Optimistic UI update
    setDrivers((prev) => prev.map((d) => (d.id === driverId ? { ...d, [counterField]: 0 } : d)));
    setSelectedDriver((prev) => (prev && prev.id === driverId ? ({ ...prev, [counterField]: 0 } as Driver) : prev));

    try {
      await driverService.resetDriverCounter(driverId, counterField);
    } catch (err: any) {
      console.error('Error resetting driver counter:', err);
      // Re-fetch to restore correct values on error
      fetchDrivers();
      throw err;
    }
  };

  const fetchDriverHistoryPage = async (
    driverId: string,
    filters?: OrderHistoryFilters,
    options?: {
      pageSize?: number;
      startAfterDocument?: any;
    }
  ) => {
    try {
      return await driverService.getDriverOrderHistoryPage(driverId, filters, options);
    } catch (err: any) {
      console.error('Error fetching driver history:', err);
      throw err;
    }
  };

  const fetchDriverHistorySummary = async (driverId: string, filters?: OrderHistoryFilters) => {
    try {
      return await driverService.getDriverHistorySummary(driverId, filters);
    } catch (err: any) {
      console.error('Error fetching driver history summary:', err);
      throw err;
    }
  };

  const fetchDriverRatingsForOrderIds = async (orderIds: string[]) => {
    try {
      return await driverService.getRatingsForOrderIds(orderIds);
    } catch (err: any) {
      console.error('Error fetching driver ratings:', err);
      throw err;
    }
  };

  const fetchOpenDriverShift = async (driverId: string) => {
    try {
      return await driverService.getOpenDriverShift(driverId);
    } catch (err: any) {
      console.error('Error fetching open driver shift:', err);
      throw err;
    }
  };

  const fetchPaidDriverShiftsPage = async (
    driverId: string,
    filters?: OrderHistoryFilters,
    options?: {
      pageSize?: number;
      startAfterDocument?: any;
    }
  ) => {
    try {
      return await driverService.getDriverPaidShiftsPage(driverId, filters, options);
    } catch (err: any) {
      console.error('Error fetching paid driver shifts:', err);
      throw err;
    }
  };

  // Ban driver handlers
  const handleOpenBanDialog = (driver: Driver) => {
    setDriverToBan(driver);
    setIsBanDialogOpen(true);
  };

  const handleCloseBanDialog = () => {
    setIsBanDialogOpen(false);
    setDriverToBan(null);
    setIsBanning(false);
  };

  const handleBanDriver = async () => {
    if (!driverToBan) return;

    try {
      setIsBanning(true);
      const success = await driverService.banDriver(driverToBan.id, banReason || undefined);

      if (success) {
        // Re-fetch drivers to get updated data
        fetchDrivers();
        handleCloseBanDialog();
        setBanReason(''); // Reset reason
      }
    } catch (err: any) {
      setError(`Error banning driver: ${err.message}`);
      console.error('Error banning driver:', err);
    } finally {
      setIsBanning(false);
    }
  };

  // Unban driver handlers
  const handleOpenUnbanDialog = (driver: Driver) => {
    setDriverToUnban(driver);
    setIsUnbanDialogOpen(true);
  };

  const handleCloseUnbanDialog = () => {
    setIsUnbanDialogOpen(false);
    setDriverToUnban(null);
    setIsUnbanning(false);
    setUnbanReason(''); // Reset reason
  };

  const handleUnbanDriver = async () => {
    if (!driverToUnban) return;

    try {
      setIsUnbanning(true);
      const success = await driverService.unbanDriver(driverToUnban.id, unbanReason || undefined);

      if (success) {
        // Re-fetch drivers to get updated data
        fetchDrivers();
        handleCloseUnbanDialog();
      }
    } catch (err: any) {
      setError(`Error unbanning driver: ${err.message}`);
      console.error('Error unbanning driver:', err);
    } finally {
      setIsUnbanning(false);
    }
  };

  // Open Google Maps with driver location - Cost-effective way to view location
  const handleViewLocationOnMap = (driver: Driver) => {
    if (driver.location) {
      const url = `https://www.google.com/maps?q=${driver.location.latitude},${driver.location.longitude}`;
      window.open(url, '_blank');
    }
  };

  // Open drivers map modal
  const handleOpenDriversMap = () => {
    setIsMapModalOpen(true);
  };

  const handleCloseMapModal = () => {
    setIsMapModalOpen(false);
  };

  const handleOpenLiveTracking = (driver: Driver) => {
    setLiveTrackingDriver(driver);
    setIsLiveTrackingOpen(true);
  };

  const handleCloseLiveTracking = () => {
    setIsLiveTrackingOpen(false);
    setLiveTrackingDriver(null);
  };

  const handleOpenAssignmentDialog = (driver: Driver) => {
    setDriverToAssign(driver);
    setAssignmentStoreId(resolveDefaultAssignmentStoreId(driver));
    setAssignmentMode(normalizeAssignmentMode(driver));
    setError(null);
    setSuccessMessage(null);
    setAssignmentDialogOpen(true);
  };

  const handleCloseAssignmentDialog = () => {
    if (isAssignmentSubmitting) {
      return;
    }

    setAssignmentDialogOpen(false);
    setDriverToAssign(null);
    setAssignmentStoreId('');
    setAssignmentMode('group_shared');
    setIsAssignmentSubmitting(false);
  };

  const handleSaveAssignment = async () => {
    if (!driverToAssign || !assignmentStoreId) {
      return;
    }

    const targetStore = storesById.get(assignmentStoreId);
    const targetStoreLabel = targetStore?.name || assignmentStoreId;
    const targetGroupLabel = formatGroupLabel(targetStore?.operationGroupId);
    const driverLabel =
      `${driverToAssign.firstName || ''} ${driverToAssign.lastName || ''}`.trim() ||
      driverToAssign.phone;

    try {
      setIsAssignmentSubmitting(true);
      setError(null);
      await driverService.assignDriverToStore(driverToAssign.id, assignmentStoreId, {
        assignmentMode,
      });
      handleCloseAssignmentDialog();
      await fetchDrivers();
      setSuccessMessage(
        assignmentMode === 'store_locked'
          ? `${driverLabel} est maintenant verrouillé sur ${targetStoreLabel} (${targetGroupLabel}).`
          : `${driverLabel} est maintenant partagé dans ${targetGroupLabel}, avec ${targetStoreLabel} comme magasin d’attache.`
      );
    } catch (err: any) {
      setError(err.message || 'Affectation impossible.');
      setIsAssignmentSubmitting(false);
    }
  };

  const handleToggleAssignmentMode = async (driver: Driver) => {
    const currentMode = normalizeAssignmentMode(driver);
    const nextMode: DriverAssignmentMode =
      currentMode === 'store_locked' ? 'group_shared' : 'store_locked';
    const targetStoreId = resolveDefaultAssignmentStoreId(driver);

    if (!targetStoreId) {
      handleOpenAssignmentDialog(driver);
      return;
    }

    const targetStore = storesById.get(targetStoreId);
    const driverLabel =
      `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || driver.phone;

    try {
      setError(null);
      setSuccessMessage(null);
      await driverService.assignDriverToStore(driver.id, targetStoreId, {
        assignmentMode: nextMode,
      });
      await fetchDrivers();
      setSuccessMessage(
        nextMode === 'store_locked'
          ? `${driverLabel} est verrouillé sur ${targetStore?.name || targetStoreId}.`
          : `${driverLabel} est repassé en partage groupe.`
      );
    } catch (err: any) {
      setError(err.message || 'Changement de mode impossible.');
    }
  };

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestion des Livreurs
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_event, nextTab) => setActiveTab(nextTab as DriverViewTab)}
          sx={{ mb: 3 }}
        >
          <Tab
            value="groupDrivers"
            label="Livreurs du groupe"
            disabled={!isGroupScopeReady}
          />
          <Tab
            value="pendingRequests"
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <span>Inscriptions en attente</span>
                {pendingRequestsCount > 0 && (
                  <Badge
                    badgeContent={pendingRequestsCount}
                    color="warning"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none',
                        minWidth: 20,
                        height: 20,
                        fontSize: 11,
                        fontWeight: 700,
                      },
                    }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {activeTab === 'groupDrivers' ? (
          !isGroupScopeReady ? (
            <ScopeRequirementAlert
              requiredScope="group"
              title="Contexte groupe requis"
              description="Cette page gère les livreurs mutualisés d'un groupe opérationnel. Sélectionne un scope Groupe depuis la barre haute."
            />
          ) : loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading drivers: {error}
            </Alert>
          ) : (
            <>

        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Rechercher un livreur…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280, maxWidth: 380, flex: '1 1 280px' }}
          />

          <Chip
            size="small"
            label="En ligne"
            icon={
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                backgroundColor: showOnlineOnly ? '#27A8AE' : '#9ca3af',
                ml: '10px !important', mr: '-4px !important',
              }} />
            }
            onClick={showOnlineOnly ? undefined : () => setShowOnlineOnly(true)}
            onDelete={showOnlineOnly ? () => setShowOnlineOnly(false) : undefined}
            deleteIcon={<CloseIcon sx={{ fontSize: '16px !important', color: '#1d8f95 !important' }} titleAccess="Retirer le filtre" />}
            sx={{
              height: 32, fontWeight: 600, cursor: showOnlineOnly ? 'default' : 'pointer',
              backgroundColor: showOnlineOnly ? 'rgba(39, 168, 174, 0.1)' : 'background.paper',
              color: showOnlineOnly ? '#1d8f95' : 'text.secondary',
              border: '1px solid',
              borderColor: showOnlineOnly ? 'rgba(39, 168, 174, 0.24)' : 'divider',
              '&:hover': { backgroundColor: showOnlineOnly ? 'rgba(39, 168, 174, 0.1)' : '#fafbfc' },
              '& .MuiChip-deleteIcon:hover': { color: '#15767b !important' },
            }}
          />

          <Chip
            size="small"
            icon={<ScheduleIcon sx={{ fontSize: '16px !important', color: showOpenShiftOnly ? '#1d8f95 !important' : 'inherit' }} />}
            label="Shift ouvert"
            onClick={showOpenShiftOnly ? undefined : () => setShowOpenShiftOnly(true)}
            onDelete={showOpenShiftOnly ? () => setShowOpenShiftOnly(false) : undefined}
            deleteIcon={<CloseIcon sx={{ fontSize: '16px !important', color: '#1d8f95 !important' }} titleAccess="Retirer le filtre" />}
            sx={{
              height: 32, fontWeight: 600, cursor: showOpenShiftOnly ? 'default' : 'pointer',
              backgroundColor: showOpenShiftOnly ? 'rgba(39, 168, 174, 0.1)' : 'background.paper',
              color: showOpenShiftOnly ? '#1d8f95' : 'text.secondary',
              border: '1px solid',
              borderColor: showOpenShiftOnly ? 'rgba(39, 168, 174, 0.24)' : 'divider',
              '&:hover': { backgroundColor: showOpenShiftOnly ? 'rgba(39, 168, 174, 0.1)' : '#fafbfc' },
              '& .MuiChip-deleteIcon:hover': { color: '#15767b !important' },
            }}
          />

          <Box sx={{ flex: 1 }} />

          <IconButton
            onClick={handleOpenDriversMap}
            disabled={drivers.filter((d) => d.availability === 'online').length === 0}
            title={`Vue carte · ${drivers.filter((d) => d.availability === 'online').length} en ligne · ${drivers.filter((d) => d.location).length}/${drivers.length} géolocalisés`}
            sx={{
              border: 1,
              borderColor: 'rgba(39, 168, 174, 0.24)',
              borderRadius: 1.5,
              p: 0.75,
              backgroundColor: 'transparent',
              color: '#1d8f95',
              '&:hover': { backgroundColor: 'transparent', borderColor: 'rgba(39, 168, 174, 0.34)' },
              '&.Mui-disabled': {
                borderColor: 'divider',
                backgroundColor: 'transparent',
                color: '#94a3b8',
              },
            }}
          >
            <Badge
              badgeContent={drivers.filter((d) => d.availability === 'online').length}
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: 10,
                  minWidth: 16,
                  height: 16,
                  px: 0.5,
                  backgroundColor: '#27A8AE',
                  color: '#ffffff',
                },
              }}
            >
              <TravelExploreIcon fontSize="small" sx={{ color: drivers.filter((d) => d.availability === 'online').length > 0 ? '#1d8f95' : '#94a3b8' }} />
            </Badge>
          </IconButton>

          <IconButton
            onClick={() => setIsOfferSettingsOpen(true)}
            title="Paramètres des offres de livraison"
            sx={{
              border: 1,
              borderColor: 'rgba(39, 168, 174, 0.24)',
              borderRadius: 1.5,
              p: 0.75,
              backgroundColor: 'transparent',
              color: '#1d8f95',
              '&:hover': { backgroundColor: 'transparent', borderColor: 'rgba(39, 168, 174, 0.34)' },
            }}
          >
            <TuneIcon fontSize="small" sx={{ color: '#1d8f95' }} />
          </IconButton>

          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewColumnIcon sx={{ color: '#1d8f95' }} />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
            sx={{
              borderColor: 'rgba(39, 168, 174, 0.24)',
              backgroundColor: 'rgba(39, 168, 174, 0.08)',
              color: '#1d8f95',
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
              py: 0.75,
              '& .MuiButton-startIcon': { color: '#1d8f95' },
              '&:hover': { backgroundColor: 'rgba(39, 168, 174, 0.14)', borderColor: 'rgba(39, 168, 174, 0.34)' },
            }}
          >
            Colonnes
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            sx={{ textTransform: 'none', fontWeight: 600, px: 2, py: 0.75, boxShadow: 'none' }}
          >
            Ajouter un livreur
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            boxShadow: '0 4px 16px rgba(15,23,42,.06), 0 2px 4px rgba(15,23,42,.04)',
            overflowX: 'auto',
          }}
        >
          <Table sx={{ minWidth: 1100, borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead>
              <TableRow
                sx={{
                  '& > th': {
                    backgroundColor: '#fafbfc',
                    fontWeight: 500,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                }}
              >
                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, boxShadow: '1px 0 0 rgba(0,0,0,0.04)' }}>Livreur</TableCell>
                {visibleColumns.online && <TableCell align="center">En ligne</TableCell>}
                {visibleColumns.activity && <TableCell align="center">Activité</TableCell>}
                {visibleColumns.shift && <TableCell align="center">Disponibilité</TableCell>}
                {visibleColumns.enabled && <TableCell align="center">Activé</TableCell>}
                {visibleColumns.rating && <TableCell align="center">Note</TableCell>}
                {visibleColumns.orders && <TableCell align="center">Commandes</TableCell>}
                {visibleColumns.assignment && <TableCell align="center">Affectation</TableCell>}
                {visibleColumns.commission && <TableCell align="center">Commission</TableCell>}
                {visibleColumns.status && <TableCell align="center">Statut</TableCell>}
                <TableCell align="right" sx={{ position: 'sticky', right: 0, zIndex: 3, boxShadow: '-1px 0 0 rgba(0,0,0,0.04)' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedDrivers.map((driver) => {
                const activityStatusMeta = getActivityStatusMeta(driver.activityStatus);
                const canResetActivityStatus = (driver.activityStatus ?? 'free') !== 'free';
                const currentAssignmentMode = normalizeAssignmentMode(driver);
                const assignedStoreLabel = formatStoreLabel(driver.assignedStoreId);
                const acceptedCount = driver.orders_accepted_count ?? 0;
                const ignoredCount = driver.orders_ignored_count ?? 0;
                const refusedCount = driver.orders_refused_count ?? 0;
                const reviewCount = driver.review_count ?? 0;
                const ratingText = typeof driver.rating === 'number' ? driver.rating.toFixed(1) : '0.0';
                const initials = `${(driver.firstName || '').charAt(0)}${(driver.lastName || '').charAt(0)}`.toUpperCase();
                const activityChipColors =
                  driver.activityStatus === 'delivering'
                    ? { bg: '#fffbeb', fg: '#d97706', border: '#fde68a' }
                    : driver.activityStatus === 'returning'
                      ? { bg: '#eff6ff', fg: '#2563eb', border: '#bfdbfe' }
                      : { bg: '#ecfdf5', fg: '#059669', border: '#d1fae5' };
                const openShift = openShiftsByDriverId[driver.id];
                const shiftDuration = openShift ? formatShiftDuration(openShift.startedAt) : '';

                return (
                <TableRow
                  key={driver.id}
                  sx={{
                    '& > td': {
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: '#f0f2f5',
                      py: 1.25,
                      whiteSpace: 'nowrap',
                    },
                    '&:hover > td': { backgroundColor: '#fafbfc' },
                    '&:last-of-type > td': { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, boxShadow: '1px 0 0 rgba(0,0,0,0.04)', minWidth: 240 }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box sx={{ position: 'relative', flexShrink: 0 }}>
                        <Avatar
                          src={driver.photoUrl}
                          alt={`${driver.firstName} ${driver.lastName}`}
                          sx={{
                            width: 40,
                            height: 40,
                            fontSize: 14,
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #27A8AE 0%, #2EB3B8 100%)',
                            color: '#ffffff',
                            border: '1px solid rgba(39, 168, 174, 0.24)',
                            boxShadow: '0 4px 12px rgba(39, 168, 174, 0.18)',
                          }}
                        >
                          {initials}
                        </Avatar>
                        <Box sx={{
                          position: 'absolute',
                          bottom: -1,
                          right: -1,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: driver.availability === 'online' ? '#059669' : '#9ca3af',
                          border: '2px solid',
                          borderColor: 'background.paper',
                        }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box display="flex" alignItems="center" gap={0.25}>
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ color: 'text.primary' }}>
                            {`${driver.firstName} ${driver.lastName}`}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleFavorite(driver.id, !driver.is_favorite)}
                            title={driver.is_favorite ? 'Retirer des favoris' : 'Marquer comme favori'}
                            sx={{ p: 0.25 }}
                          >
                            {driver.is_favorite
                              ? <StarIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                              : <StarBorderIcon sx={{ fontSize: 16, color: 'grey.400' }} />}
                          </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {driver.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {visibleColumns.online && (
                    <TableCell align="center">
                      <Box
                        component="span"
                        onClick={driver.isSuspended
                          ? undefined
                          : () => handleSetAvailability(driver.id, driver.availability === 'online' ? 'offline' : 'online')
                        }
                        title={driver.isSuspended
                          ? 'Livreur banni : disponibilité verrouillée'
                          : driver.availability === 'online'
                            ? 'Cliquer pour rendre hors ligne'
                            : 'Cliquer pour mettre en ligne'
                        }
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          fontWeight: 500,
                          userSelect: 'none',
                          cursor: driver.isSuspended ? 'not-allowed' : 'pointer',
                          opacity: driver.isSuspended ? 0.6 : 1,
                          color: driver.availability === 'online' ? '#059669' : '#6b7280',
                          '&:hover': driver.isSuspended ? undefined : { filter: 'brightness(0.85)' },
                        }}
                      >
                        <Box sx={{
                          width: 13,
                          height: 13,
                          minWidth: 13,
                          borderRadius: '50%',
                          background: driver.availability === 'online'
                            ? 'radial-gradient(circle at 32% 32%, #6ee7b7 0%, #10b981 42%, #059669 100%)'
                            : 'radial-gradient(circle at 32% 32%, #fca5a5 0%, #ef4444 42%, #dc2626 100%)',
                          border: '1.5px solid #ffffff',
                          boxShadow: 'none',
                        }} />
                        {driver.availability === 'online' ? 'En ligne' : 'Hors ligne'}
                      </Box>
                    </TableCell>
                  )}

                  {visibleColumns.activity && (
                    <TableCell align="center">
                      <Box
                        component="span"
                        onClick={canResetActivityStatus ? () => handleSetActivityStatusFree(driver) : undefined}
                        title={canResetActivityStatus ? 'Cliquer pour libérer le livreur' : 'Livreur déjà libre'}
                        sx={{
                          display: 'inline-block',
                          fontWeight: 500,
                          userSelect: 'none',
                          cursor: canResetActivityStatus ? 'pointer' : 'default',
                          color: activityChipColors.fg,
                          '&:hover': canResetActivityStatus ? { filter: 'brightness(0.85)' } : undefined,
                        }}
                      >
                        {activityStatusMeta.label}
                      </Box>
                    </TableCell>
                  )}

                  {visibleColumns.shift && (
                    <TableCell align="center">
                      {openShift ? (
                        <Box
                          component="span"
                          title={shiftDuration ? `Shift en cours depuis ${shiftDuration}` : 'Shift ouvert'}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 500,
                            color: '#0f766e',
                          }}
                        >
                          <ScheduleIcon sx={{ fontSize: 16 }} />
                          {shiftDuration ? `Shift ouvert · ${shiftDuration}` : 'Shift ouvert'}
                        </Box>
                      ) : (
                        <Box component="span" sx={{ color: '#9ca3af' }}>
                          Shift fermé
                        </Box>
                      )}
                    </TableCell>
                  )}

                  {visibleColumns.enabled && (
                    <TableCell align="center">
                      <Switch
                        checked={driver.enabled}
                        disabled={driver.isSuspended}
                        onChange={(e) => handleToggleEnabled(driver.id, e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                  )}

                  {visibleColumns.rating && (
                    <TableCell align="center">
                      <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        <StarIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                        <Typography component="span" variant="body2" fontWeight={500}>{ratingText}</Typography>
                        <Typography component="span" variant="caption" color="text.secondary">· {reviewCount}</Typography>
                      </Box>
                    </TableCell>
                  )}

                  {visibleColumns.orders && (
                    <TableCell align="center">
                      <Box
                        display="inline-flex"
                        alignItems="center"
                        title={`${acceptedCount} acceptées · ${ignoredCount} ignorées · ${refusedCount} refusées`}
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        <Typography component="span" sx={{ color: '#059669', fontWeight: 500 }}>{acceptedCount}</Typography>
                        <Typography component="span" sx={{ color: 'grey.300', mx: 0.75 }}>·</Typography>
                        <Typography component="span" sx={{ color: '#d97706', fontWeight: 500 }}>{ignoredCount}</Typography>
                        <Typography component="span" sx={{ color: 'grey.300', mx: 0.75 }}>·</Typography>
                        <Typography component="span" sx={{ color: '#dc2626', fontWeight: 500 }}>{refusedCount}</Typography>
                      </Box>
                    </TableCell>
                  )}

                  {visibleColumns.assignment && (
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={assignedStoreLabel}
                        icon={currentAssignmentMode === 'store_locked'
                          ? <LockOutlinedIcon sx={{ fontSize: '14px !important', color: '#d97706 !important', ml: '8px !important' }} />
                          : undefined
                        }
                        title={currentAssignmentMode === 'store_locked'
                          ? `Verrouillé sur ${assignedStoreLabel}`
                          : `Partagé dans le groupe · attache: ${assignedStoreLabel}`
                        }
                        sx={{
                          fontWeight: 400,
                          height: 26,
                          maxWidth: 180,
                          backgroundColor: currentAssignmentMode === 'store_locked' ? '#fffbeb' : '#f3f4f6',
                          color: currentAssignmentMode === 'store_locked' ? '#d97706' : '#4b5563',
                          border: '1px solid',
                          borderColor: currentAssignmentMode === 'store_locked' ? '#fde68a' : '#e5e7eb',
                          '& .MuiChip-label': { textOverflow: 'ellipsis', overflow: 'hidden' },
                        }}
                      />
                    </TableCell>
                  )}

                  {visibleColumns.commission && (
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={500} sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
                        {driver.driverPourcentage ?? 'N/A'}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>%</Typography>
                      </Typography>
                    </TableCell>
                  )}

                  {visibleColumns.status && (
                    <TableCell align="center">
                      {driver.isSuspended ? (
                        <Chip
                          size="small"
                          label="Banni"
                          icon={<BlockIcon sx={{ fontSize: '14px !important', color: '#dc2626 !important' }} />}
                          title={`Raison: ${driver.suspensionReason || 'Non spécifiée'}`}
                          sx={{ fontWeight: 500, height: 26, backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Actif"
                          icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#059669 !important' }} />}
                          sx={{ fontWeight: 500, height: 26, backgroundColor: '#ecfdf5', color: '#059669', border: '1px solid #d1fae5' }}
                        />
                      )}
                    </TableCell>
                  )}

                  <TableCell align="right" sx={{ position: 'sticky', right: 0, zIndex: 1, boxShadow: '-1px 0 0 rgba(0,0,0,0.04)', px: 1 }}>
                    <Box display="inline-flex" alignItems="center" gap={0.25}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(driver)}
                        title="Voir l'historique et les notations"
                      >
                        <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditDialog(driver)}
                        title="Modifier"
                      >
                        <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenActionMenu(e, driver)}
                        title="Plus d'actions"
                      >
                        <MoreVertIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredDrivers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Livreurs par page"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
        />
            </>
          )
        ) : (
          <PendingDriverRequestsPanel
            onDriverAssigned={async () => {
              await fetchPendingRequestsCount();
              if (isGroupScopeReady) {
                await fetchDrivers();
              }
            }}
            onPendingCountChange={setPendingRequestsCount}
          />
        )}

        {/* Driver Form Dialog */}
        <DriverFormDialog
          open={isFormDialogOpen}
          onClose={handleCloseFormDialog}
          driver={currentDriver} // Pass currentDriver for editing
          onSubmit={handleSubmitForm}
          loading={isSubmitting}
          error={submitError}
        />

        {/* Driver Details Dialog */}
        <DriverDetailsDialog
          open={isDetailsDialogOpen}
          onClose={handleCloseDetailsDialog}
          driver={selectedDriver} // Pass the selected driver
          fetchHistoryPage={fetchDriverHistoryPage}
          fetchHistorySummary={fetchDriverHistorySummary}
          fetchRatingsForOrderIds={fetchDriverRatingsForOrderIds}
          fetchOpenShift={fetchOpenDriverShift}
          fetchPaidShiftsPage={fetchPaidDriverShiftsPage}
          onResetCounter={handleResetDriverCounter}
        />

        <Dialog
          open={assignmentDialogOpen}
          onClose={handleCloseAssignmentDialog}
          aria-labelledby="driver-assignment-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="driver-assignment-dialog-title">
            Affectation du livreur
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Choisis le magasin d’attache du livreur. En mode partagé, il reste
              disponible pour tous les magasins du meme groupe. En mode verrouillé,
              il ne reçoit que les commandes de ce magasin.
            </DialogContentText>

            {driverToAssign && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  {`${driverToAssign.firstName || ''} ${
                    driverToAssign.lastName || ''
                  }`.trim() || driverToAssign.phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Groupe actuel: {formatGroupLabel(driverToAssign.groupId)}
                </Typography>
              </Box>
            )}

            <TextField
              select
              fullWidth
              label="Magasin d’attache"
              value={assignmentStoreId}
              onChange={(event) => setAssignmentStoreId(event.target.value)}
              sx={{ mb: 2 }}
            >
              {stores
                .filter((store) => !!store.operationGroupId)
                .map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name} - {formatGroupLabel(store.operationGroupId)}
                    {!store.isActive ? ' - inactif' : ''}
                  </MenuItem>
                ))}
            </TextField>

            <FormControlLabel
              control={
                <Checkbox
                  checked={assignmentMode === 'store_locked'}
                  onChange={(event) =>
                    setAssignmentMode(
                      event.target.checked ? 'store_locked' : 'group_shared'
                    )
                  }
                />
              }
              label="Verrouiller ce livreur sur le magasin sélectionné"
            />

            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Mode: ${
                  assignmentMode === 'store_locked' ? 'Verrouillé magasin' : 'Partage groupe'
                }`}
                color={assignmentMode === 'store_locked' ? 'warning' : 'primary'}
                variant={assignmentMode === 'store_locked' ? 'filled' : 'outlined'}
              />
              {assignmentStoreId && (
                <Chip
                  label={`Cible: ${formatStoreLabel(assignmentStoreId)}`}
                  variant="outlined"
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignmentDialog} disabled={isAssignmentSubmitting}>
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSaveAssignment()}
              disabled={isAssignmentSubmitting || !assignmentStoreId}
              startIcon={isAssignmentSubmitting ? <CircularProgress size={18} /> : <SwapHorizIcon />}
            >
              {isAssignmentSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Drivers Live Map Modal (online only, real-time) */}
        <DriversLiveMapModal
          open={isMapModalOpen}
          onClose={handleCloseMapModal}
        />

        {/* Single Driver Live Tracking Modal */}
        <DriverLiveTrackingModal
          open={isLiveTrackingOpen}
          onClose={handleCloseLiveTracking}
          driverId={liveTrackingDriver?.id || ''}
          driverName={`${liveTrackingDriver?.firstName || ''} ${liveTrackingDriver?.lastName || ''}`.trim() || undefined}
          driverPhotoUrl={liveTrackingDriver?.photoUrl || undefined}
          initialLocation={
            liveTrackingDriver?.location
              ? { latitude: liveTrackingDriver.location.latitude, longitude: liveTrackingDriver.location.longitude }
              : undefined
          }
        />

        <DeliveryOfferSettingsDialog
          open={isOfferSettingsOpen}
          onClose={() => setIsOfferSettingsOpen(false)}
        />

        {/* Ban Confirmation Dialog */}
        <Dialog
          open={isBanDialogOpen}
          onClose={handleCloseBanDialog}
          aria-labelledby="ban-driver-dialog-title"
          aria-describedby="ban-driver-dialog-description"
        >
          <DialogTitle id="ban-driver-dialog-title">
            Confirmer le Bannissement
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="ban-driver-dialog-description">
              {driverToBan ?
                `Voulez-vous vraiment bannir le livreur "${driverToBan.firstName} ${driverToBan.lastName}" ?` :
                'Voulez-vous vraiment bannir ce livreur ?'
              }
              <br /><br />
              Cette action va définitivement bannir le livreur et lui empêcher d'accéder à l'application.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Raison du bannissement (optionnel)"
              type="text"
              fullWidth
              variant="outlined"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="Ex: Comportement inapproprié, non respect des règles..."
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBanDialog} color="primary">
              Annuler
            </Button>
            <Button
              onClick={handleBanDriver}
              color="error"
              disabled={isBanning}
              startIcon={isBanning && <CircularProgress size={20} />}
            >
              {isBanning ? 'Bannissement...' : 'Bannir'}
            </Button>
          </DialogActions>
        </Dialog>

        <Menu
          anchorEl={columnsAnchor}
          open={Boolean(columnsAnchor)}
          onClose={() => setColumnsAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            elevation: 8,
            sx: { minWidth: 240, mt: 0.5, borderRadius: 2, boxShadow: '0 10px 30px rgba(15,23,42,.12)', px: 0.5, py: 0.5 },
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', px: 1.5, py: 0.75, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, fontSize: 11 }}>
            Afficher les colonnes
          </Typography>
          {DRIVER_COLUMNS.map((col) => (
            <MenuItem
              key={col.key}
              onClick={() => toggleColumn(col.key)}
              sx={{ py: 0.5, borderRadius: 1 }}
            >
              <Checkbox checked={visibleColumns[col.key]} size="small" sx={{ p: 0.5, mr: 0.5 }} />
              <ListItemText
                primary={col.label}
                primaryTypographyProps={{ fontSize: 13, color: 'text.primary' }}
              />
            </MenuItem>
          ))}
        </Menu>

        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor) && Boolean(actionMenuDriver)}
          onClose={handleCloseActionMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            elevation: 8,
            sx: { minWidth: 260, mt: 0.5, borderRadius: 2, boxShadow: '0 10px 30px rgba(15,23,42,.12)' },
          }}
        >
          {actionMenuDriver && [
            <MenuItem
              key="tracking"
              disabled={actionMenuDriver.availability !== 'online'}
              onClick={() => {
                const d = actionMenuDriver;
                handleCloseActionMenu();
                if (d) handleOpenLiveTracking(d);
              }}
            >
              <ListItemIcon>
                <MyLocationIcon fontSize="small" color={actionMenuDriver.availability === 'online' ? 'primary' : 'disabled'} />
              </ListItemIcon>
              <ListItemText>Suivi temps réel</ListItemText>
            </MenuItem>,
            <MenuItem
              key="gmaps"
              disabled={!actionMenuDriver.location}
              onClick={() => {
                const d = actionMenuDriver;
                handleCloseActionMenu();
                if (d) handleViewLocationOnMap(d);
              }}
            >
              <ListItemIcon>
                <LocationOnIcon fontSize="small" color={actionMenuDriver.location ? 'success' : 'disabled'} />
              </ListItemIcon>
              <ListItemText>Voir sur Google Maps</ListItemText>
            </MenuItem>,
            <Divider key="div1" sx={{ my: 0.5 }} />,
            <MenuItem
              key="lock"
              onClick={() => {
                const d = actionMenuDriver;
                handleCloseActionMenu();
                if (d) void handleToggleAssignmentMode(d);
              }}
            >
              <ListItemIcon>
                {normalizeAssignmentMode(actionMenuDriver) === 'store_locked'
                  ? <LockOpenOutlinedIcon fontSize="small" color="primary" />
                  : <LockOutlinedIcon fontSize="small" color="warning" />}
              </ListItemIcon>
              <ListItemText>
                {normalizeAssignmentMode(actionMenuDriver) === 'store_locked'
                  ? 'Repasser en partage groupe'
                  : 'Verrouiller sur le magasin'}
              </ListItemText>
            </MenuItem>,
            <MenuItem
              key="swap"
              onClick={() => {
                const d = actionMenuDriver;
                handleCloseActionMenu();
                if (d) handleOpenAssignmentDialog(d);
              }}
            >
              <ListItemIcon>
                <SwapHorizIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Changer l'affectation</ListItemText>
            </MenuItem>,
            <Divider key="div2" sx={{ my: 0.5 }} />,
            actionMenuDriver.isSuspended ? (
              <MenuItem
                key="unban"
                onClick={() => {
                  const d = actionMenuDriver;
                  handleCloseActionMenu();
                  if (d) handleOpenUnbanDialog(d);
                }}
              >
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText>Débannir le livreur</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem
                key="ban"
                onClick={() => {
                  const d = actionMenuDriver;
                  handleCloseActionMenu();
                  if (d) handleOpenBanDialog(d);
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <BlockIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Bannir le livreur</ListItemText>
              </MenuItem>
            ),
            <MenuItem
              key="delete"
              onClick={() => {
                const d = actionMenuDriver;
                handleCloseActionMenu();
                if (d) void handleDeleteDriver(d.id);
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Supprimer</ListItemText>
            </MenuItem>,
          ]}
        </Menu>

        {/* Unban Confirmation Dialog */}
        <Dialog
          open={isUnbanDialogOpen}
          onClose={handleCloseUnbanDialog}
          aria-labelledby="unban-driver-dialog-title"
          aria-describedby="unban-driver-dialog-description"
        >
          <DialogTitle id="unban-driver-dialog-title">
            Confirmer le Débannissement
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="unban-driver-dialog-description">
              {driverToUnban ?
                `Voulez-vous vraiment débannir le livreur "${driverToUnban.firstName} ${driverToUnban.lastName}" ?` :
                'Voulez-vous vraiment débannir ce livreur ?'
              }
              <br /><br />
              Cette action va réactiver le compte du livreur et lui permettre à nouveau d'accéder à l'application.
            </DialogContentText>
            {driverToUnban?.suspensionReason && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Raison du bannissement :</strong> {driverToUnban.suspensionReason}
                </Typography>
              </Box>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Raison du débannissement (optionnel)"
              type="text"
              fullWidth
              variant="outlined"
              value={unbanReason}
              onChange={(e) => setUnbanReason(e.target.value)}
              placeholder="Ex: Problème résolu, nouvelle chance accordée..."
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUnbanDialog} color="primary">
              Annuler
            </Button>
            <Button
              onClick={handleUnbanDriver}
              color="success"
              disabled={isUnbanning}
              startIcon={isUnbanning && <CircularProgress size={20} />}
            >
              {isUnbanning ? 'Débannissement...' : 'Débannir'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default Drivers;
